import React, { useState, useEffect } from 'react';
import { 
  PhoneCall, MessageSquare, Clock, CheckCircle, Search, Filter, 
  RefreshCw, User, UserCheck, XCircle, ShieldCheck, CornerDownRight, 
  RotateCcw, Calendar, MoreVertical, Edit3, Check, AlertCircle 
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import { useAuth } from '@/App';

const AllParentsRequests = () => {
  const { user } = useAuth();
  const hostelId = user?.hostel_id;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Action modal states for Admin (Reject, Postpone, General update)
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    requestId: null,
    actionType: '', // 'reject' | 'postpone' | 'accept'
    reason: '',
    note: ''
  });
  const [submittingAction, setSubmittingAction] = useState(false);

  useEffect(() => {
    if (hostelId) {
      fetchRequests();
    }
  }, [hostelId, selectedStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `/v1/admin/parent-requests/?hostel_id=${hostelId}`;
      if (selectedStatus) url += `&status=${selectedStatus}`;
      const response = await apiClient.get(url);
      setRequests(response.data || []);
    } catch (err) {
      toast.error('Failed to load parent requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, status, note = null, reason = null) => {
    try {
      const payload = { status, note, reason };
      const res = await apiClient.post(`/v1/admin/parent-requests/update-status/${requestId}`, payload);
      if (res.data?.success) {
        toast.success(`Request status successfully updated to ${status}`);
        setActionModal({ isOpen: false, requestId: null, actionType: '', reason: '', note: '' });
        fetchRequests();
      } else {
        toast.error(res.data?.message || 'Failed to update request');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update request status');
    }
  };

  const handleOpenActionModal = (req, actionType) => {
    setActionModal({
      isOpen: true,
      requestId: req.id,
      actionType,
      reason: actionType === 'reject' ? (req.rejection_reason || '') : (req.postponed_reason || ''),
      note: req.admin_note || ''
    });
  };

  const handleConfirmModalAction = async (e) => {
    e.preventDefault();
    if (actionModal.actionType === 'reject' && !actionModal.reason.trim()) {
      toast.error('Please provide a reason for rejecting this request.');
      return;
    }
    if (actionModal.actionType === 'postponed' && !actionModal.reason.trim()) {
      toast.error('Please provide a postponement reason or reschedule time.');
      return;
    }

    setSubmittingAction(true);
    try {
      await handleUpdateStatus(
        actionModal.requestId,
        actionModal.actionType,
        actionModal.note || actionModal.reason,
        actionModal.reason
      );
    } finally {
      setSubmittingAction(false);
    }
  };

  const filteredRequests = requests.filter(r => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      r.parent_name?.toLowerCase().includes(query) ||
      r.student_name?.toLowerCase().includes(query) ||
      r.parent_phone?.includes(query) ||
      r.reason?.toLowerCase().includes(query) ||
      r.rejection_reason?.toLowerCase().includes(query) ||
      r.warden_note?.toLowerCase().includes(query) ||
      r.admin_note?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white gap-1"><Clock className="w-3 h-3" /> PENDING</Badge>;
      case 'accepted':
      case 'approved':
        return <Badge className="bg-emerald-600 text-white gap-1"><CheckCircle className="w-3 h-3" /> ACCEPTED</Badge>;
      case 'postponed':
        return <Badge className="bg-blue-600 text-white gap-1"><Clock className="w-3 h-3" /> POSTPONED</Badge>;
      case 'rejected':
        return <Badge className="bg-rose-600 text-white gap-1"><XCircle className="w-3 h-3" /> REJECTED</Badge>;
      case 'completed':
      case 'done':
        return <Badge className="bg-slate-700 text-white gap-1"><CheckCircle className="w-3 h-3" /> COMPLETED</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-400 text-white gap-1"><XCircle className="w-3 h-3" /> CANCELLED</Badge>;
      default:
        return <Badge variant="outline">{status?.toUpperCase() || 'UNKNOWN'}</Badge>;
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted' || r.status === 'approved').length;
  const postponedCount = requests.filter(r => r.status === 'postponed').length;
  const completedCount = requests.filter(r => r.status === 'completed' || r.status === 'done').length;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="admin-parent-requests">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <PhoneCall className="w-8 h-8 text-blue-600" />
            All Parents Request Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Admin master control: review, approve, postpone, complete, or override warden decisions on parent requests
          </p>
        </div>
        <Button onClick={fetchRequests} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh List
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card 
          onClick={() => setSelectedStatus('')} 
          className={`cursor-pointer transition-all border ${selectedStatus === '' ? 'ring-2 ring-blue-500 bg-blue-50/50' : 'hover:bg-slate-50'}`}
        >
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase">Total</p>
              <h3 className="text-xl font-bold text-slate-800">{requests.length}</h3>
            </div>
            <MessageSquare className="w-6 h-6 text-blue-500" />
          </CardContent>
        </Card>

        <Card 
          onClick={() => setSelectedStatus('pending')} 
          className={`cursor-pointer transition-all border ${selectedStatus === 'pending' ? 'ring-2 ring-amber-500 bg-amber-50/50' : 'hover:bg-slate-50'}`}
        >
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-amber-700 uppercase">Pending</p>
              <h3 className="text-xl font-bold text-amber-900">{pendingCount}</h3>
            </div>
            <Clock className="w-6 h-6 text-amber-500" />
          </CardContent>
        </Card>

        <Card 
          onClick={() => setSelectedStatus('accepted')} 
          className={`cursor-pointer transition-all border ${selectedStatus === 'accepted' ? 'ring-2 ring-emerald-500 bg-emerald-50/50' : 'hover:bg-slate-50'}`}
        >
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-emerald-700 uppercase">Accepted</p>
              <h3 className="text-xl font-bold text-emerald-900">{acceptedCount}</h3>
            </div>
            <CheckCircle className="w-6 h-6 text-emerald-500" />
          </CardContent>
        </Card>

        <Card 
          onClick={() => setSelectedStatus('rejected')} 
          className={`cursor-pointer transition-all border ${selectedStatus === 'rejected' ? 'ring-2 ring-rose-500 bg-rose-50/50' : 'hover:bg-slate-50'}`}
        >
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-rose-700 uppercase">Rejected</p>
              <h3 className="text-xl font-bold text-rose-900">{rejectedCount}</h3>
            </div>
            <XCircle className="w-6 h-6 text-rose-500" />
          </CardContent>
        </Card>

        <Card 
          onClick={() => setSelectedStatus('completed')} 
          className={`cursor-pointer transition-all border ${selectedStatus === 'completed' ? 'ring-2 ring-slate-500 bg-slate-100' : 'hover:bg-slate-50'}`}
        >
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-600 uppercase">Completed</p>
              <h3 className="text-xl font-bold text-slate-800">{completedCount}</h3>
            </div>
            <Check className="w-6 h-6 text-slate-600" />
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter Bar */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search parent, student, reason, note..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-slate-500 font-semibold flex items-center gap-1">
              <Filter className="w-3.5 h-3.5" /> Filter:
            </span>
            <select
              className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All Statuses ({requests.length})</option>
              <option value="pending">Pending ({pendingCount})</option>
              <option value="accepted">Accepted ({acceptedCount})</option>
              <option value="postponed">Postponed ({postponedCount})</option>
              <option value="rejected">Rejected ({rejectedCount})</option>
              <option value="completed">Completed ({completedCount})</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Request Cards Feed */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading parent requests...</div>
        ) : filteredRequests.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <UserCheck className="w-12 h-12 mx-auto text-blue-400 mb-2" />
            <p className="text-slate-600 font-semibold">No parent requests found</p>
            <p className="text-slate-400 text-xs mt-1">There are no requests matching your current filter.</p>
          </Card>
        ) : (
          filteredRequests.map((req) => (
            <Card key={req.id} className="border border-slate-200 hover:shadow-md transition-all overflow-hidden bg-white">
              <CardContent className="p-5 space-y-4">
                {/* Header Badge Row */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-blue-600 text-white">{req.request_type || 'Call Request'}</Badge>
                    {getStatusBadge(req.status)}
                    {req.priority && (
                      <Badge variant={req.priority === 'Urgent' ? 'destructive' : 'outline'}>
                        {req.priority} Priority
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(req.created_at).toLocaleDateString()} {new Date(req.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                </div>

                {/* Audit: Warden & Admin Action Highlights */}
                {req.updated_by_role === 'admin' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-xs text-purple-900 flex items-start gap-2.5">
                    <ShieldCheck className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <span className="font-bold">🛡️ Admin Override / Action Active:</span>
                      <p className="text-purple-800">
                        Status was set to <strong className="uppercase font-bold">{req.status}</strong> by Administrator <strong>{req.updated_by_name}</strong>
                        {req.updated_at && ` on ${new Date(req.updated_at).toLocaleDateString()}`}.
                      </p>
                      {req.admin_note && (
                        <p className="text-purple-700 italic font-medium">"Admin Note: {req.admin_note}"</p>
                      )}
                    </div>
                  </div>
                )}

                {req.updated_by_role === 'warden' && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-700 flex items-start gap-2.5">
                    <User className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <span className="font-bold text-slate-800">👤 Warden Action Recorded:</span>
                      <p className="text-slate-700">
                        Handled by Warden <strong>{req.updated_by_name || 'Assigned Warden'}</strong> with status <strong className="uppercase">{req.status}</strong>.
                      </p>
                      {req.warden_note && (
                        <p className="text-slate-600 italic font-medium">"Warden Note: {req.warden_note}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Parent and Student Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/70 p-3.5 rounded-xl border border-slate-100 text-xs md:text-sm">
                  <div className="flex items-center gap-2 text-slate-800 font-bold">
                    <User className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>Parent: {req.parent_name}</span>
                    <span className="text-slate-500 font-normal">({req.parent_phone})</span>
                  </div>
                  <div className="text-slate-700">
                    Student: <span className="font-bold text-slate-900">{req.student_name}</span> ({req.class_name || 'Class N/A'}{req.room_number ? `, Room ${req.room_number}` : ''})
                  </div>
                </div>

                {/* Request Reason / Details */}
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Parent Request Reason</h4>
                  <p className="text-slate-800 text-sm font-medium leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                    {req.reason}
                  </p>
                </div>

                {/* What, When, Who Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-slate-50/50 p-3 rounded-xl border border-slate-100 text-xs">
                  <div>
                    <span className="font-bold text-slate-600">🎯 Action Requested:</span>
                    <p className="text-slate-800 font-semibold">{req.what_to_happen || 'Warden Call-back'}</p>
                  </div>
                  <div>
                    <span className="font-bold text-slate-600">📅 Preferred Time / Date:</span>
                    <p className="text-slate-800 font-semibold">{req.preferred_time || req.when_date || 'As soon as possible'}</p>
                  </div>
                  <div>
                    <span className="font-bold text-slate-600">👤 Contact Target:</span>
                    <p className="text-slate-800 font-semibold">{req.who_person || `${req.parent_name} (${req.parent_phone})`}</p>
                  </div>
                </div>

                {/* Rejection Reason Display Banner */}
                {(req.status === 'rejected' || req.rejection_reason) && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 text-xs md:text-sm text-rose-900 flex items-start gap-2.5">
                    <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-1">
                        <span className="font-bold text-rose-800">❌ Rejection Reason:</span>
                        {req.updated_by_name && (
                          <span className="text-[11px] text-rose-600 font-medium">
                            Rejected by: {req.updated_by_name} ({req.updated_by_role?.toUpperCase() || 'WARDEN'})
                          </span>
                        )}
                      </div>
                      <p className="text-rose-900 font-bold bg-white/80 p-2.5 rounded-lg border border-rose-200">
                        {req.rejection_reason || (req.warden_note ? req.warden_note.replace(/^Rejected:\s*/i, '') : req.admin_note || 'No specific reason provided.')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Postponed Reason Display Banner */}
                {(req.status === 'postponed' || req.postponed_reason) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-xs md:text-sm text-blue-900 flex items-start gap-2.5">
                    <Clock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-1">
                        <span className="font-bold text-blue-800">🕒 Postponed / Rescheduled:</span>
                        {req.updated_by_name && (
                          <span className="text-[11px] text-blue-600 font-medium">
                            Updated by: {req.updated_by_name} ({req.updated_by_role?.toUpperCase() || 'WARDEN'})
                          </span>
                        )}
                      </div>
                      <p className="text-blue-900 font-bold bg-white/80 p-2.5 rounded-lg border border-blue-200">
                        {req.postponed_reason || (req.warden_note ? req.warden_note.replace(/^Postponed:\s*/i, '') : req.admin_note)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Admin Master Action Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 bg-slate-50/50 p-3 rounded-xl">
                  {/* Left: Call Parent link */}
                  <a
                    href={`tel:${req.parent_phone}`}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-xs"
                  >
                    <PhoneCall className="w-3.5 h-3.5 text-blue-600" /> Call Parent ({req.parent_phone})
                  </a>

                  {/* Right: Admin Action / Override Buttons (Always accessible regardless of warden actions) */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold text-slate-500 uppercase mr-1 hidden sm:inline">
                      Admin Action:
                    </span>

                    {/* Action 1: Accept / Approve (Available on any status other than accepted) */}
                    {req.status !== 'accepted' && (
                      <Button
                        onClick={() => handleUpdateStatus(req.id, 'accepted', 'Approved by Administrator')}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1 font-bold shadow-xs"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        {req.status === 'rejected' ? 'Override & Accept' : 'Accept'}
                      </Button>
                    )}

                    {/* Action 2: Postpone with Reschedule Reason (Always available) */}
                    <Button
                      onClick={() => handleOpenActionModal(req, 'postponed')}
                      variant="outline"
                      size="sm"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs gap-1 font-medium"
                    >
                      <Clock className="w-3.5 h-3.5 text-blue-600" />
                      {req.status === 'postponed' ? 'Edit Reschedule...' : 'Postpone...'}
                    </Button>

                    {/* Action 3: Reject with Reason (Always available to reject or edit rejection) */}
                    <Button
                      onClick={() => handleOpenActionModal(req, 'reject')}
                      variant="outline"
                      size="sm"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50 text-xs gap-1 font-medium"
                    >
                      <XCircle className="w-3.5 h-3.5 text-rose-600" />
                      {req.status === 'rejected' ? 'Edit Rejection Reason...' : 'Reject...'}
                    </Button>

                    {/* Action 4: Complete / Mark Done */}
                    {req.status !== 'completed' && req.status !== 'done' && (
                      <Button
                        onClick={() => handleUpdateStatus(req.id, 'completed', 'Marked completed by Administrator')}
                        size="sm"
                        className="bg-slate-800 hover:bg-slate-900 text-white text-xs gap-1 font-bold shadow-xs"
                      >
                        <Check className="w-3.5 h-3.5 text-emerald-400" /> Complete
                      </Button>
                    )}

                    {/* Action 5: Reset to Pending (if request was previously processed) */}
                    {req.status !== 'pending' && (
                      <Button
                        onClick={() => handleUpdateStatus(req.id, 'pending', 'Reset to pending by Administrator')}
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-slate-800 text-xs gap-1"
                        title="Reopen request and mark as Pending"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset Pending
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Admin Action Modal Dialog for Reject and Postpone */}
      <Dialog open={actionModal.isOpen} onOpenChange={(open) => !open && setActionModal(prev => ({ ...prev, isOpen: false }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              {actionModal.actionType === 'reject' ? (
                <>
                  <XCircle className="w-5 h-5 text-rose-600" />
                  Admin Rejection / Decision
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5 text-blue-600" />
                  Admin Postpone / Reschedule
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleConfirmModalAction} className="space-y-4 pt-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">
                {actionModal.actionType === 'reject' ? 'Rejection Reason (Visible to Parent & Warden) *' : 'Postponed Note / New Scheduled Time *'}
              </label>
              <textarea
                rows={3}
                required
                placeholder={
                  actionModal.actionType === 'reject'
                    ? 'Explain why this parent request is being rejected or why the warden decision was overridden...'
                    : 'Provide the reschedule time and reason for postponement...'
                }
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={actionModal.reason}
                onChange={(e) => setActionModal({ ...actionModal, reason: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">
                Internal Admin Note (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Overrode warden decision after talking to guardian"
                className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                value={actionModal.note}
                onChange={(e) => setActionModal({ ...actionModal, note: e.target.value })}
              />
            </div>

            <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setActionModal(prev => ({ ...prev, isOpen: false }))}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingAction}
                className={actionModal.actionType === 'reject' ? 'bg-rose-600 hover:bg-rose-700 text-white font-bold' : 'bg-blue-600 hover:bg-blue-700 text-white font-bold'}
              >
                {submittingAction ? 'Updating...' : `Confirm ${actionModal.actionType === 'reject' ? 'Rejection' : 'Postpone'}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllParentsRequests;
